import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faCalendarAlt, faMusic } from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { collection, doc, getDoc, getDocs, query, where, serverTimestamp, addDoc } from 'firebase/firestore';
import TourContext from '../../context/TourContext';
import { sendEventInvitationNotification } from '../../utils/NotificationService';
import styles from '../ManagerDashboard/Profile.module.css';

const TourInvitationModal = ({ isOpen, onClose, userData, currentUser, profileData }) => {
  // Add TourContext at the component level
  const { events, fetchEventsData, updateEventData, loadMoreEvents, hasMoreEvents } = useContext(TourContext);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDateAssignments, setEventDateAssignments] = useState({});
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [eventType, setEventType] = useState('all'); // 'all', 'tour', or 'show'
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const db = getFirestoreInstance();

  // Filter events based on user permissions
  useEffect(() => {
    if (!events || !Array.isArray(events)) return;

    const filtered = events.filter(event => {
      // Check if user is the creator
      if (event.createdBy?.userId === currentUser.userId) {
        return true;
      }

      // Check all possible user types for canAssignUsers permission
      // Check in assignedUsers collection first (new structure)
      if (event.assignedUsers) {
        const userEntry = event.assignedUsers.find(user => user.userId === currentUser.userId);
        if (userEntry?.permissions?.canAssignUsers) {
          return true;
        }
  
        // Check if user has full control permission
        if (userEntry?.permissions?.canEditTourDetails &&
            userEntry?.permissions?.canEditSchedule &&
            userEntry?.permissions?.canAddAttachments &&
            userEntry?.permissions?.canAssignTasks &&
            userEntry?.permissions?.canAssignUsers) {
          return true;
        }
      }
  
        return false;
      });

    // Apply event type filter if needed
    const typeFiltered = eventType === 'all' 
      ? filtered 
      : filtered.filter(event => event.type === eventType);

    setFilteredEvents(typeFiltered);
  }, [events, currentUser.userId, eventType]);

// Add handleLoadMore function
const handleLoadMore = async () => {
  if (isLoadingMore) return;
  
  setIsLoadingMore(true);
  try {
    await loadMoreEvents(userData);
  } catch (error) {
    console.error('Error loading more events:', error);
  } finally {
    setIsLoadingMore(false);
  }
};

// Function to check if a date has a pending invitation
const checkPendingInvitation = (event, dateDocId) => {
  if (!event?.pendingInvitationsData || !Array.isArray(event.pendingInvitationsData)) {
    return false;
  }

  return event.pendingInvitationsData.some(invitation => 
    invitation.userId === profileData.userId && 
    invitation.status === 'pending' &&
    invitation.assignedDates?.some(assignment => assignment.dateDocId === dateDocId)
  );
};

const checkIfUserOnEvent = (events, userId, date) => {
  const dateString = date.toISOString().split('T')[0];
  return events.some(event => {
    // Find the user in assignedUsers
    const assignedUser = event.assignedUsers?.find(user => user.userId === userId);
    
    // Check if the user is assigned to this date
    return assignedUser && event.eventDates?.some(eventDate => {
      const eventDateStr = new Date(eventDate.date).toISOString().split('T')[0];
      return eventDateStr === dateString && 
             assignedUser.assignedDates?.some(assignment => 
               assignment.dateDocId === eventDate.id && 
               assignment.status === 'accepted'
             );
    });
  });
};

const handleEventSelect = async (event) => {
  setSelectedEvent(event);
  setIsLoading(true);
  setEventDateAssignments({});

  try {
    const db = getFirestoreInstance();
    const collectionName = event.type === 'tour' ? 'tours' : 'shows';
    
    // Fetch all dates from the eventDates subcollection
    const eventDatesRef = collection(db, collectionName, event.id, 'eventDates');
    const eventDatesSnapshot = await getDocs(eventDatesRef);
    
    const eventDates = eventDatesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Fetch pending invitations for this user
    const pendingInvitationsQuery = query(
      collection(db, collectionName, event.id, 'pendingInvitations'),
      where('userId', '==', profileData.userId)
    );
    
    const pendingInvitationsSnapshot = await getDocs(pendingInvitationsQuery);
    const pendingInvitationsData = pendingInvitationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Add pendingInvitationsData to event
    const updatedEvent = {
      ...event,
      pendingInvitationsData
    };

    // Process each date to determine its status
    const existingAssignments = {};
    
    // Check if user is already assigned to the event
    const isUserAssigned = event.assignedUsers?.some(user => user.userId === profileData.userId);
    
    eventDates.forEach(date => {
      // Check if this date is already assigned to the user
      const isAssigned = isUserAssigned && 
                        event.assignedUsers
                          .find(user => user.userId === profileData.userId)
                          ?.assignedDates
                          ?.some(assignment => 
                            assignment.dateDocId === date.id && 
                            assignment.status === 'accepted'
                          );
      
      // Use the checkPendingInvitation function
      const isPending = checkPendingInvitation(updatedEvent, date.id);
      
      // Check if user is on another event on this date
      const dateObj = new Date(date.date);
      const isOnOtherEvent = checkIfUserOnEvent(events, profileData.userId, dateObj);

      existingAssignments[date.id] = {
        date: date.date,
        isAssigned: false, // Start unselected, user will select dates to invite
        isAlreadyAssigned: isAssigned,
        isPending: isPending,
        isOnOtherEvent: isOnOtherEvent,
        dateDocId: date.id
      };
    });

    setEventDateAssignments(existingAssignments);
    setSelectedEvent({...updatedEvent, dates: eventDates});
  } catch (error) {
    console.error(`Error fetching ${event.type} dates:`, error);
  } finally {
    setIsLoading(false);
  }
};

const handleDateSelection = (dateId) => {
  const dateInfo = eventDateAssignments[dateId];
  if (dateInfo?.isOnOtherEvent || dateInfo?.isPending || dateInfo?.isAlreadyAssigned) return;
  
  setEventDateAssignments(prev => ({
    ...prev,
    [dateId]: {
      ...prev[dateId],
      isAssigned: !prev[dateId]?.isAssigned
    }
  }));
};

const renderDateItem = (date) => {
  const dateId = date.id;
  const assignment = eventDateAssignments[dateId];
  if (!assignment) return null;
  
  const isPending = assignment.isPending;
  const isAlreadyAssigned = assignment.isAlreadyAssigned;
  const isOnOtherEvent = assignment.isOnOtherEvent;
  
  return (
    <div 
      key={dateId}
      className={`${styles.dateItem} 
        ${isOnOtherEvent ? styles.onTour : ''} 
        ${isPending ? styles.pending : ''} 
        ${isAlreadyAssigned ? styles.alreadyAssigned : ''}
        ${assignment.isAssigned ? styles.selected : ''}`}
      onClick={() => !isOnOtherEvent && !isPending && !isAlreadyAssigned && handleDateSelection(dateId)}
    >
      <div className={styles.dateInfo}>
        <span>{new Date(assignment.date).toLocaleDateString()}</span>
        <span className={styles.status}>
          {isAlreadyAssigned ? 'Already Assigned' :
           isOnOtherEvent ? 'On Another Event' : 
           isPending ? 'Pending' :
           'Available'}
        </span>
      </div>
      {date.event?.venueDetails?.venueData && (
        <div className={styles.venueInfo}>
          <p className={styles.venueName}>{date.event.venueDetails.venueData.displayName}</p>
          <p className={styles.venueLocation}>{date.event.venueDetails.venueData.formattedAddress}</p>
        </div>
      )}
    </div>
  );
};

  const handleSendInvitation = async () => {
    try {
      setIsUpdating(true);
      
      // Get selected dates
      const selectedDates = Object.entries(eventDateAssignments)
        .filter(([dateId, details]) => details.isAssigned)
        .map(([dateId, details]) => ({
          date: details.date,
          dateDocId: dateId
        }));
  
      if (selectedDates.length === 0) {
        alert('Please select at least one date');
        setIsUpdating(false);
        return;
      }
      
      const collectionName = selectedEvent.type === 'tour' ? 'tours' : 'shows';
      
      // Determine permissions based on user type
      const permissions = profileData.userType === 'Artist' 
        ? {
            canEditSchedule: false,
            canAddAttachments: true,
            canAssignTasks: false,
            canEditSetlist: true,
            canManageGuestlist: true,
            canManageHotels: false,
            canManageTickets: false,
            canAssignUsers: false
          }
        : profileData.userType === 'Crew'
          ? {
              canEditSchedule: true,
              canAddAttachments: true,
              canAssignTasks: true,
              canEditSetlist: false,
              canManageGuestlist: true,
              canManageHotels: true,
              canManageTickets: true,
              canAssignUsers: false
            }
          : profileData.userType === 'Manager'
            ? {
                canEditSchedule: true,
                canAddAttachments: true,
                canAssignTasks: true,
                canEditSetlist: true,
                canManageGuestlist: true,
                canManageHotels: true,
                canManageTickets: true,
                canAssignUsers: true
              }
            : profileData.userType === 'Organization'
              ? {
                  canEditSchedule: false,
                  canAddAttachments: true,
                  canAssignTasks: false,
                  canEditSetlist: false,
                  canManageGuestlist: true,
                  canManageHotels: false,
                  canManageTickets: false,
                  canAssignUsers: false
                }
              : {
                  canEditSchedule: false,
                  canAddAttachments: false,
                  canAssignTasks: false,
                  canEditSetlist: false,
                  canManageGuestlist: false,
                  canManageHotels: false,
                  canManageTickets: false,
                  canAssignUsers: false
                };
  
      // Create invitation data
      const invitationData = {
        userId: profileData.userId,
        userName: profileData.userName,
        fullName: profileData.fullName,
        email: profileData.email || '',
        profilePictureUrl: profileData.profilePictureUrl || '',
        userType: profileData.userType,
        userSubType: profileData.userSubType,
        role: profileData.userType,
        permissions,
        assignedDates: selectedDates,
        status: 'pending',
        timestamp: serverTimestamp(),
        eventType: selectedEvent.type,
        eventId: selectedEvent.id,
        tourId: selectedEvent.id, // For backward compatibility
        tourName: selectedEvent.tourName,
        showName: selectedEvent.type === 'show' ? selectedEvent.showName || selectedEvent.tourName : null,
        actName: selectedEvent.actName,
        actType: selectedEvent.actType,
        createdBy: {
          userId: currentUser.userId,
          fullName: currentUser.fullName,
          userName: currentUser.userName,
          email: currentUser.email,
          profilePictureUrl: currentUser.profilePictureUrl,
          userType: currentUser.userType,
          userSubType: currentUser.userSubType
        }
      };
  
      // Add to pendingInvitations subcollection
      const invitationRef = await addDoc(
        collection(db, collectionName, selectedEvent.id, 'pendingInvitations'), 
        invitationData
      );
    
      // Update local state to mark dates as pending
      const updatedAssignments = { ...eventDateAssignments };
      selectedDates.forEach(({ dateDocId }) => {
        updatedAssignments[dateDocId] = {
          ...updatedAssignments[dateDocId],
          isPending: true,
          isAssigned: false,
        };
      });
      setEventDateAssignments(updatedAssignments);
  
      // Update event data
      await updateEventData(selectedEvent.id, selectedEvent.type);
  
      // Send notification to the user
      await sendEventInvitationNotification(profileData.userId, {
        ...invitationData,
        invitationId: invitationRef.id
      });
  
      alert(`${selectedEvent.type === 'tour' ? 'Tour' : 'Show'} invitation sent successfully!`);
      onClose();
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert('Error sending invitation. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {!selectedEvent ? (
          <div className={styles['upload-options']}>
            <h2>Choose Event</h2>
            
            {/* Event type filter */}
            <div className={styles.eventTypeFilter}>
              <button 
                className={`${styles.filterButton} ${eventType === 'all' ? styles.active : ''}`}
                onClick={() => setEventType('all')}
              >
                All Events
              </button>
              <button 
                className={`${styles.filterButton} ${eventType === 'tour' ? styles.active : ''}`}
                onClick={() => setEventType('tour')}
              >
                <FontAwesomeIcon icon={faCalendarAlt} /> Tours
              </button>
              <button 
                className={`${styles.filterButton} ${eventType === 'show' ? styles.active : ''}`}
                onClick={() => setEventType('show')}
              >
                <FontAwesomeIcon icon={faMusic} /> Shows
              </button>
            </div>
            
            <div className={styles['tour-list']}>
              {filteredEvents.length === 0 ? (
                <div className={styles.noEvents}>
                  <p>No events found where you have permission to invite users.</p>
                </div>
              ) : (
                filteredEvents.map(event => {
                  // Check if user is already on this event or has a pending invitation
                  const isPending = event.pendingInvitationsData?.some(inv => 
                    inv.userId === profileData.userId && inv.status === 'pending'
                  );
                  
                  const isAccepted = event.assignedUsers?.some(user => 
                    user.userId === profileData.userId
                  );

                  return (
                    <div 
                      key={event.id} 
                      className={`${styles['tour-item']} ${isPending || isAccepted ? styles.disabled : ''}`}
                      onClick={() => !isPending && !isAccepted && handleEventSelect(event)}
                    >
                      <img 
                        src={event.tourCoverPhotoUrl || require('../../assets/Images/dafaulttourcoverphoto.png')}
                        alt={event.tourName || event.showName}
                        className={styles['tour-cover']}
                      />
                      <div className={styles['tour-item-content']}>
                        <div>
                          <h3>{event.type === 'show' ? event.showName || event.tourName : event.tourName}</h3>
                          <p>{event.actName}</p>
                          <span className={styles.eventType}>
                            {event.type === 'tour' ? 'Tour' : 'Show'}
                          </span>
                        </div>
                        {(isPending || isAccepted) && (
                          <div className={styles['tour-status']}>
                            {isPending ? 'Request Pending' : 'Already Added'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Add Load More button */}
            {hasMoreEvents && (
              <button 
                className={styles['load-more-button']}
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <span>Load More Events</span>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className={styles['tour-info']}>
            <h2>{selectedEvent.type === 'show' ? selectedEvent.showName || selectedEvent.tourName : selectedEvent.tourName}</h2>
            <p className={styles['act-name']}>{selectedEvent.actName}</p>
            <p className={styles['event-type']}>{selectedEvent.type === 'tour' ? 'Tour' : 'Show'}</p>
            
            {isLoading ? (
              <div className={styles.loadingDates}>
                <FontAwesomeIcon icon={faSpinner} spin />
                <p>Loading dates...</p>
              </div>
            ) : (
              <div className={styles['dates-list']}>
                {selectedEvent.dates && selectedEvent.dates.length > 0 ? (
                  selectedEvent.dates.map(renderDateItem)
                ) : (
                  <div className={styles.noDates}>
                    <p>No dates available for this {selectedEvent.type}.</p>
                  </div>
                )}
              </div>
            )}
            
            <div className={styles['form-actions']}>
              <button 
                className={styles['secondary-button']} 
                onClick={() => setSelectedEvent(null)}
              >
                Back
              </button>
              <button 
                className={styles['primary-button']}
                onClick={handleSendInvitation}
                disabled={
                  isUpdating || 
                  !Object.values(eventDateAssignments).some(a => a.isAssigned) ||
                  isLoading
                }
              >
                {isUpdating ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TourInvitationModal;